import React, { useEffect, useState } from "react";
import { useInstanceListQuery } from "generated/graphql";
import {
  doAllInstancesHaveLicense,
  InstanceModel
} from "utilities/licenseServerUtils";
import { useTranslation } from "utilities/i18n";
import { infiniteNestLink, Link, Route, Switch } from "polyrouter";
import { Layout } from "views/Main/Layouts";
import {
  HEADER_ID,
  itemCheckedWithSet,
  sortTableDataByValueModel,
  TableBody,
  TableBulkItemHeader,
  TableBulkItemRow,
  TableHeaderItem,
  toggleAll,
  toggleItemWithSet
} from "collections/Table";
import { CreateInstanceModal } from "../Create/CreateInstanceModal";
import { ProcessLicenseRequestModal } from "../License/ProcessLicenseRequestModal";
import { ProcessLicenseAckModal } from "../License/ProcessLicenseAckModal";
import { CreateAltInstanceModal } from "../Create/CreateAltInstanceModal";
import { ReleaseLicenseModal } from "../License/ReleaseLicenseModal";
import { GetLicenseModal } from "../License/GetLicenseModal";
import { DeleteInstanceModal } from "../Delete/DeleteInstanceModal";
import { SortState } from "store/filters";
import { getProductDetailsLink } from "../../ProductCatalog/View/ProductCatalogDetails";
import { DropdownButton } from "elements";

export const InstanceRow: React.FC<{
  instance: InstanceModel;
  checked: boolean;
  onCheck: () => void;
}> = ({ instance, checked, onCheck }) => {
  return (
    <TableBulkItemRow checked={checked} onCheck={() => onCheck()}>
      <td>
        <Link
          to={`/manage/licensing/clariti/purchased-products/details/${instance.recordId}`}
        >
          {instance.name.text}
        </Link>
      </td>
      <td>
        <Link
          to={getProductDetailsLink(
            instance.productId.data,
            instance.productVersion.data
          )}
        >
          {instance.productId.text}
        </Link>
      </td>
      <td>{instance.productVersion.text}</td>
      <td>{instance.serialNumber.text}</td>
      <td>{instance.hostName.text}</td>
      <td>{instance.allEntitlements.length}</td>
      <td>{instance.lastModifiedTime.text}</td>
    </TableBulkItemRow>
  );
};

export const InstanceList: React.FC<{
  tenantId: string;
}> = ({ tenantId }) => {
  const [selectedItems, setSelectedItems] = useState<Array<string>>([]);
  const sortByName = "name.sortValue";
  const [sort, setSort] = useState<SortState>({
    field: sortByName,
    direction: "ASC"
  });

  const { Content } = Layout;

  useEffect(() => {
    setSelectedItems([]);
  }, [tenantId]);

  const { data, fetching } = useInstanceListQuery({
    variables: {
      tenantId
    },
    pause: !tenantId
  });

  const instances: Array<InstanceModel> =
    data?.searchResult.edges?.map(entry => {
      return new InstanceModel(entry);
    }) ?? [];

  sortTableDataByValueModel(instances, sort);

  const toggleItem = toggleItemWithSet(
    instances.length,
    selectedItems,
    setSelectedItems
  );

  const itemChecked = itemCheckedWithSet(selectedItems);

  const selectedInstances = selectedItems.reduce((InstancesList, recordId) => {
    const instanceObj = instances.find(
      instance => instance.recordId === recordId
    );
    if (instanceObj) {
      InstancesList.push(instanceObj);
    }
    return InstancesList;
  }, [] as Array<InstanceModel>);

  const allSelectedInstancesHaveLicense = doAllInstancesHaveLicense(
    selectedInstances
  );

  const refresh = () => {
    setSelectedItems([]);
  };

  const { t } = useTranslation();
  return (
    <>
      <div className="grouping pb-1">
        <DropdownButton className="btn btn-primary" title={t("ADD_NEW")}>
          <Link to={infiniteNestLink("/add-standard")}>
            {t("ADD_NEW_INSTANCE")}
          </Link>
          <Link to={infiniteNestLink("/add-restrictive")}>
            {t("ADD_NEW_ALT_INSTANCE")}
          </Link>
        </DropdownButton>
        <DropdownButton
          disabled={selectedInstances.length !== 1}
          className="btn btn-secondary"
          title={t("LICENSE_INSTANCE")}
        >
          <Link to={infiniteNestLink("/process-license-request")}>
            {t("PROCESS_LICENSE_REQUEST")}
          </Link>
          <Link
            to={
              allSelectedInstancesHaveLicense
                ? infiniteNestLink("/process-license-ack")
                : ""
            }
            className={`${allSelectedInstancesHaveLicense ? "" : "disabled"}`}
          >
            {t("PROCESS_LICENSE_ACK")}
          </Link>
          <Link
            to={
              allSelectedInstancesHaveLicense
                ? infiniteNestLink("/release-license")
                : ""
            }
            className={`${allSelectedInstancesHaveLicense ? "" : "disabled"}`}
          >
            {t("RELEASE_LICENSE")}
          </Link>
          <Link
            to={
              allSelectedInstancesHaveLicense
                ? infiniteNestLink("/get-license")
                : ""
            }
            className={`${allSelectedInstancesHaveLicense ? "" : "disabled"}`}
          >
            {t("GET_LICENSE")}
          </Link>
        </DropdownButton>
        <Link
          to={selectedInstances.length > 0 ? infiniteNestLink("/remove") : ""}
          className={`btn ${selectedInstances.length > 0 ? "" : "disabled"}`}
        >
          {t("REMOVE")}
        </Link>
      </div>
      <Content.ScrollingList>
        <table className="table custom-table">
          <TableBulkItemHeader
            checked={itemChecked(HEADER_ID)}
            onCheck={() =>
              toggleAll(
                instances.map(instance => instance?.recordId),
                selectedItems,
                setSelectedItems
              )
            }
          >
            <TableHeaderItem
              fieldName="name.sortValue"
              title={t("NAME")}
              sort={{
                currentSort: sort,
                onSort: setSort
              }}
            />
            <TableHeaderItem
              fieldName="productId.sortValue"
              title={t("TYPE")}
              sort={{
                currentSort: sort,
                onSort: setSort
              }}
            />
            <TableHeaderItem
              fieldName="productVersion.sortValue"
              title={t("VERSION_ALT")}
              sort={{
                currentSort: sort,
                onSort: setSort
              }}
            />
            <TableHeaderItem
              fieldName="serialNumber.sortValue"
              title={t("SERIAL_NUMBER")}
              sort={{
                currentSort: sort,
                onSort: setSort
              }}
            />
            <TableHeaderItem
              fieldName="hostName.sortValue"
              title={t("HOST_NAME")}
              sort={{
                currentSort: sort,
                onSort: setSort
              }}
            />
            <TableHeaderItem
              fieldName="allEntitlements.length"
              title={t("INSTANCE_ENTITLEMENT_COUNT")}
              sort={{
                currentSort: sort,
                onSort: setSort
              }}
            />
            <TableHeaderItem
              fieldName="lastModifiedTime.sortValue"
              title={t("LAST_MODIFIED")}
              sort={{
                currentSort: sort,
                onSort: setSort
              }}
            />
          </TableBulkItemHeader>
          <TableBody loading={fetching} cols={6}>
            {instances.map(instance => {
              return (
                <InstanceRow
                  instance={instance}
                  key={instance.recordId}
                  checked={itemChecked(instance.recordId)}
                  onCheck={() => toggleItem(instance.recordId)}
                />
              );
            })}
          </TableBody>
        </table>
      </Content.ScrollingList>
      <Switch>
        <Route
          nest
          path="/add-standard"
          render={() => (
            <CreateInstanceModal tenantId={tenantId} refresh={refresh} />
          )}
        />
        <Route
          nest
          path="/add-restrictive"
          render={() => (
            <CreateAltInstanceModal tenantId={tenantId} refresh={refresh} />
          )}
        />
        <Route nest path="/remove">
          <DeleteInstanceModal
            instances={selectedInstances}
            refresh={refresh}
          />
        </Route>
        <Route
          nest
          path="/process-license-request"
          render={() => (
            <ProcessLicenseRequestModal instances={selectedInstances} />
          )}
        />
        <Route
          nest
          path="/process-license-ack"
          render={() => (
            <ProcessLicenseAckModal
              instances={selectedInstances}
              refresh={refresh}
            />
          )}
        />
        <Route
          nest
          path="/get-license"
          render={() => <GetLicenseModal instances={selectedInstances} />}
        />
        <Route
          nest
          path="/release-license"
          render={() => (
            <ReleaseLicenseModal
              instances={selectedInstances}
              refresh={refresh}
            />
          )}
        />
      </Switch>
    </>
  );
};
